import { useEffect, useRef, useState, type ReactNode } from 'react';

import { api, ApiError } from '../../api/client';
import { hashQuery, loadPendingYandexRegistration } from '../../features/auth/yandex';
import { loadPendingVkRegistration } from '../../features/auth/vk';
import { loadPendingOidc } from '../../features/auth/oidc';
import {
  useRegistrationDraft,
  type CarrierEntityType,
  type RegistrationDraft,
} from '../../features/registration/model';
import { useFigmaBreakpoint, type FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { useAuth, type SessionUser } from '../../state/auth';
import { Button, Footer, Header, Input, InternationalPhoneInput, MobileDrawer, Tile, WizardButton } from '../../ui';
import { RegistrationEmailStep } from '../../features/registration/ui/RegistrationEmailStep';
import { RegistrationTypeStep } from '../../features/registration/ui/RegistrationTypeStep';
import { RegistrationSmsStep } from '../../features/registration/ui/RegistrationSmsStep';
import { RegistrationPasswordStep } from '../../features/registration/ui/RegistrationPasswordStep';
import type { ScreenId } from '../../app/screenRoutes';
import '../RequestWizard/request-wizard.css';
import '../StandaloneAuth/standalone-auth.css';
import './carrier-registration.css';

export type CarrierRegistrationScreenId = Extract<
  ScreenId,
  | 'carrier-rega-email'
  | 'carrier-rega-type'
  | 'carrier-rega-contact'
  | 'carrier-rega-contact-company'
  | 'carrier-rega-sent'
  | 'carrier-rega-password'
>;

const SCREEN_NODES: Record<CarrierRegistrationScreenId, Partial<Record<FigmaBreakpoint, string>>> = {
  'carrier-rega-email': { '1920': '2824:51204', '1440': '2824:51345' },
  'carrier-rega-type': { '1920': '2787:49810', '1440': '2787:50051' },
  'carrier-rega-contact': { '1920': '2787:49837', '1440': '2787:50086' },
  'carrier-rega-contact-company': { '1920': '2787:49900', '1440': '2787:50115' },
  'carrier-rega-sent': { '1920': '2787:49929', '1440': '2787:50144' },
  'carrier-rega-password': { '1920': '2787:50333', '1440': '2787:50181' },
};

const STEP_DETAILS: Record<
  CarrierRegistrationScreenId,
  { step: number; label: string; previous?: CarrierRegistrationScreenId }
> = {
  'carrier-rega-email': { step: 1, label: 'email' },
  'carrier-rega-type': { step: 2, label: 'тип аккаунта', previous: 'carrier-rega-email' },
  'carrier-rega-contact': { step: 3, label: 'контакты', previous: 'carrier-rega-type' },
  'carrier-rega-contact-company': { step: 3, label: 'контакты', previous: 'carrier-rega-type' },
  'carrier-rega-sent': { step: 4, label: 'подтверждение телефона', previous: 'carrier-rega-contact' },
  'carrier-rega-password': { step: 5, label: 'пароль', previous: 'carrier-rega-sent' },
};

function navigate(screen: ScreenId) {
  window.location.hash = `#${screen}`;
}

function errorMessage(error: unknown): string {
  let msg = '';
  if (error instanceof ApiError && error.payload && typeof error.payload === 'object') {
    const first = Object.values(error.payload as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length) msg = String(first[0]);
    else if (typeof first === 'string') msg = first;
  }
  if (!msg) {
    msg = error instanceof Error ? error.message : 'Не удалось выполнить действие';
  }
  if (/invalid email or password/i.test(msg)) return 'Неверный email или пароль';
  return msg.replace(/\.+$/, '');
}

function CarrierProgress({
  label,
  previous,
  step,
}: {
  label: string;
  previous?: CarrierRegistrationScreenId;
  step: number;
}) {
  return (
    <div
      className={`request-wizard__progress standalone-auth__progress${previous ? '' : ' standalone-auth__progress--first'}`}
      data-figma-node="124:32118"
    >
      <div className="request-wizard__progress-bars" data-figma-variant={`Step=${step}`}>
        {Array.from({ length: 6 }, (_, index) => <i className={index < step ? 'is-active' : ''} key={index} />)}
      </div>
      <div className="request-wizard__progress-meta">
        {previous ? (
          <Button kind="tertiary" size="medium" icon="left" onClick={() => navigate(previous)}>Назад</Button>
        ) : <span aria-hidden="true" />}
        <span className="standalone-auth__progress-label">
          <b>{`Регистрация перевозчика (${step}/6)`}</b><i aria-hidden="true" /><span>{label}</span>
        </span>
      </div>
    </div>
  );
}

function CarrierShell({ children, screen }: { children: ReactNode; screen: CarrierRegistrationScreenId }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const breakpoint = useFigmaBreakpoint();
  const node = SCREEN_NODES[screen][breakpoint]
    ?? SCREEN_NODES[screen]['1440']
    ?? SCREEN_NODES[screen]['1920'];
  const step = STEP_DETAILS[screen];

  return (
    <section
      className={`request-wizard standalone-auth carrier-registration carrier-registration--${screen}`}
      data-screen={screen}
      data-figma-node={node}
      data-figma-variant={breakpoint}
    >
      <div className="request-wizard__stage standalone-auth__stage">
        <Header
          menuExpanded={menuOpen}
          externalDrawer
          onMenuToggle={() => setMenuOpen((v) => !v)}
          onLogoClick={() => navigate('landing')}
          onLogin={() => navigate('login')}
          onCarrierRegistration={() => navigate('carrier-rega-email')}
        />
        {menuOpen ? (
          <MobileDrawer
            variant="no log in"
            activeAction="carrier-registration"
            onClose={() => setMenuOpen(false)}
            onLogoClick={() => navigate('landing')}
            onNavigate={(action) => {
              setMenuOpen(false);
              if (action === 'carrier-registration') navigate('carrier-rega-email');
              else if (action === 'login') navigate('login');
              else navigate('landing');
            }}
          />
        ) : null}
        <div className="request-wizard__layout standalone-auth__layout">
          <div className="request-wizard__image standalone-auth__image" role="img" aria-label="Дорога к месту назначения" />
          <main className="request-wizard__form-shell standalone-auth__form-shell">
            <CarrierProgress label={step.label} previous={step.previous} step={step.step} />
            {children}
          </main>
        </div>
      </div>
      <Footer onNavigate={(action) => {
        if (action === 'logo') navigate('landing');
        if (action === 'customer-cabinet') navigate('my-requests');
        if (action === 'carrier-cabinet') navigate('carrier-dashboard');
        if (action === 'carrier-registration') navigate('carrier-rega-email');
      }} />
    </section>
  );
}

function FormError({ message }: { message: string }) {
  return message ? <p className="request-wizard__error standalone-auth__error" role="alert">{message.replace(/\.+$/, '')}</p> : null;
}

function CarrierEmailScreen({
  draft,
  updateDraft,
}: {
  draft: RegistrationDraft;
  updateDraft: (patch: Partial<RegistrationDraft>) => void;
}) {
  return (
    <RegistrationEmailStep
      role="carrier"
      draft={draft}
      updateDraft={updateDraft}
      figmaNode="2824:51358"
      onExistingUser={() => navigate('login')}
      onNewUser={() => navigate('carrier-rega-type')}
      onSwitchRole={() => navigate('customer-rega-email')}
    />
  );
}

function CarrierTypeScreen({
  draft,
  updateDraft,
}: {
  draft: RegistrationDraft;
  updateDraft: (patch: Partial<RegistrationDraft>) => void;
}) {
  return (
    <RegistrationTypeStep
      role="carrier"
      draft={draft}
      updateDraft={updateDraft}
      figmaNode="2824:51495"
      onSelectCustomerType={() => {}}
      onSelectCarrierType={(carrierEntityType) => {
        navigate(carrierEntityType === 'person' ? 'carrier-rega-contact' : 'carrier-rega-contact-company');
      }}
    />
  );
}

function CarrierContactScreen({
  company,
  draft,
  updateDraft,
}: {
  company: boolean;
  draft: RegistrationDraft;
  updateDraft: (patch: Partial<RegistrationDraft>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sendCode = async () => {
    const missing = company
      ? !draft.companyName.trim() || !draft.contactName.trim()
      : !draft.firstName.trim() || !draft.lastName.trim();
    if (missing || !draft.phone.trim()) return setError('Заполните контактные данные и телефон.');
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', {
        method: 'POST',
        body: { phone: draft.phone.trim(), purpose: 'register' },
      });
      updateDraft({ smsCode: result.debug_code ?? '', debugSmsCode: result.debug_code ?? '' });
      navigate('carrier-rega-sent');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`carrier-registration__form carrier-registration__form--contact${company ? ' carrier-registration__form--company' : ''}`}
      data-figma-node={company ? '2787:50129' : '2787:50100'}
    >
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>{company ? 'Расскажите о компании' : 'Расскажите о себе'}</h1>
        <p>{company ? 'ИП / Компания · одно дополнительное поле — название организации' : 'Частное лицо · имя видят заказчики, телефон — только после принятия отклика'}</p>
      </div>
      <div className="carrier-registration__contact-row">
        {company ? (
          <>
            <Input density="compact" label="Название компании или ИП" placeholder="ИП Кузнецов Д.М. / ООО «Лапы»" value={draft.companyName} onChange={(event) => updateDraft({ companyName: event.target.value })} />
            <Input density="compact" label="Имя контактного лица" placeholder="Дмитрий Кузнецов" value={draft.contactName} onChange={(event) => updateDraft({ contactName: event.target.value })} />
          </>
        ) : (
          <>
            <Input density="compact" label="Имя" placeholder="Дмитрий" value={draft.firstName} onChange={(event) => updateDraft({ firstName: event.target.value })} />
            <Input density="compact" label="Фамилия" placeholder="Кузнецов" value={draft.lastName} onChange={(event) => updateDraft({ lastName: event.target.value })} />
          </>
        )}
        <div className="carrier-registration__phone-field">
          <InternationalPhoneInput density="compact" label="Телефон" value={draft.phone} onChange={(phone) => updateDraft({ phone })} />
          <p>{company ? 'Передаём перевозчику только после того, как вы примете отклик' : 'Будем присылать SMS об активных перевозках'}</p>
        </div>
      </div>
      <div className="carrier-registration__contact-actions">
        <WizardButton disabled={busy} onClick={() => void sendCode()}>{busy ? 'Отправляем…' : 'Создать аккаунт'}</WizardButton>
        <p>{company ? 'ИНН, ОГРН и реквизиты загрузите в профиле после регистрации' : 'Документы, автомобиль и портфолио загрузите на следующем шаге — без них нельзя откликаться'}</p>
      </div>
      <FormError message={error} />
    </div>
  );
}

function CodeInputs({ code, onChange }: { code: string; onChange: (value: string) => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  return (
    <div className="request-wizard__code-row standalone-auth__code-row">
      {Array.from({ length: 6 }, (_, index) => (
        <input
          aria-label={`Цифра ${index + 1}`}
          inputMode="numeric"
          key={index}
          maxLength={1}
          ref={(node) => { refs.current[index] = node; }}
          value={code[index] ?? ''}
          className={code[index] ? 'is-filled' : ''}
          onChange={(event) => {
            const digit = event.target.value.replace(/\D/g, '').slice(-1);
            const chars = code.padEnd(6, ' ').split('');
            chars[index] = digit || ' ';
            onChange(chars.join('').trimEnd());
            if (digit && index < 5) refs.current[index + 1]?.focus();
          }}
        />
      ))}
    </div>
  );
}

function CarrierSmsScreen({
  draft,
  updateDraft,
}: {
  draft: RegistrationDraft;
  updateDraft: (patch: Partial<RegistrationDraft>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(42);
  const [error, setError] = useState('');

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCountdown]);

  const verify = async () => {
    if (draft.smsCode.replace(/\s/g, '').length !== 6) return setError('Введите шестизначный код.');
    setBusy(true);
    setError('');
    try {
      await api('/api/auth/sms/verify/', {
        method: 'POST',
        body: { phone: draft.phone, purpose: 'register', code: draft.smsCode },
      });
      navigate('carrier-rega-password');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', {
        method: 'POST',
        body: { phone: draft.phone, purpose: 'register' },
      });
      updateDraft({ smsCode: result.debug_code ?? '', debugSmsCode: result.debug_code ?? '' });
      setResendCountdown(42);
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="carrier-registration__form carrier-registration__form--sms" data-figma-node="2787:50158">
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Введите код из SMS</h1>
        <p>Отправили на {draft.phone}. Подтверждаем телефон — email подтвердите потом в профиле.</p>
      </div>
      <CodeInputs code={draft.smsCode} onChange={(smsCode) => updateDraft({ smsCode })} />
      <div className="request-wizard__sms-actions standalone-auth__sms-actions">
        <WizardButton disabled={busy} onClick={() => void verify()}>{busy ? 'Проверяем…' : 'Подтвердить'}</WizardButton>
        <Button
          aria-disabled={resendCountdown > 0}
          kind="tertiary"
          size="medium"
          onClick={() => {
            if (resendCountdown === 0) void resend();
          }}
        >
          {resendCountdown > 0
            ? `Не пришёл код? Отправить снова через 0:${String(resendCountdown).padStart(2, '0')}`
            : 'Не пришёл код? Отправить снова'}
        </Button>
        <Button kind="tertiary" size="medium" onClick={() => navigate(draft.accountType === 'company' ? 'carrier-rega-contact-company' : 'carrier-rega-contact')}>Сменить номер телефона</Button>
      </div>
      <FormError message={error} />
    </div>
  );
}

function CarrierPasswordScreen({ draft }: { draft: RegistrationDraft }) {
  const { setAuthenticatedUser } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const register = async () => {
    if (password.length < 8 || !/[A-ZА-Я]/.test(password) || !/\d/.test(password)) return setError('Пароль: минимум 8 символов, одна заглавная буква и одна цифра.');
    if (password !== repeat) return setError('Пароли не совпадают.');
    setBusy(true);
    setError('');
    try {
      const user = await api<SessionUser>('/api/auth/register/', {
        method: 'POST',
        body: {
          role: 'carrier',
          email: draft.email,
          password,
          phone: draft.phone,
          sms_code: draft.smsCode,
          account_type: draft.accountType,
          first_name: draft.firstName,
          last_name: draft.lastName,
          company_name: draft.companyName,
          contact_name: draft.contactName,
        },
      });
      setAuthenticatedUser(user);
      navigate('carrier-verify');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="carrier-registration__form carrier-registration__form--password"
      data-figma-node="2787:50195"
      onSubmit={(e) => {
        e.preventDefault();
        void register();
      }}
    >
      <div className="standalone-auth__copy">
        <h1>Придумайте пароль</h1>
        <p>Понадобится для входа с других устройств</p>
      </div>
      <div className="standalone-auth__password-content">
        <div className="request-wizard__success-box">
          <strong>✓ Телефон подтверждён: {draft.phone}</strong>
          <span>После создания аккаунта отправим подтверждение на {draft.email}</span>
        </div>
        <div className="request-wizard__password-fields">
          <Input density="compact" label="Пароль" type="password" autoComplete="new-password" placeholder="Минимум 8 символов" value={password} onChange={(event) => setPassword(event.target.value)} />
          <p>Латиница, цифры, минимум одна заглавная буква</p>
          <Input density="compact" label="Повторите пароль" type="password" autoComplete="new-password" placeholder="Повторите" value={repeat} onChange={(event) => setRepeat(event.target.value)} />
        </div>
      </div>
      <WizardButton type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</WizardButton>
      <FormError message={error} />
    </form>
  );
}

export function CarrierRegistrationScreen({ screen }: { screen: CarrierRegistrationScreenId }) {
  const { draft, updateDraft } = useRegistrationDraft();

  useEffect(() => {
    const provider = hashQuery().get('oauth');
    if (provider !== 'yandex' && provider !== 'vk' && provider !== 'google' && provider !== 'apple') return;
    const loadPending = provider === 'google' || provider === 'apple' ? () => loadPendingOidc(provider) : provider === 'vk' ? loadPendingVkRegistration : loadPendingYandexRegistration;
    void loadPending()
      .then((pending) => updateDraft({
        targetRole: 'carrier',
        email: pending.email,
        firstName: pending.first_name,
        lastName: pending.last_name,
      }))
      .catch(() => undefined);
  }, [screen, updateDraft]);
  let content: ReactNode = null;

  if (screen === 'carrier-rega-email') content = <CarrierEmailScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'carrier-rega-type') content = <CarrierTypeScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'carrier-rega-contact') content = <CarrierContactScreen company={false} draft={draft} updateDraft={updateDraft} />;
  if (screen === 'carrier-rega-contact-company') content = <CarrierContactScreen company draft={draft} updateDraft={updateDraft} />;
  if (screen === 'carrier-rega-sent') {
    content = (
      <RegistrationSmsStep
        draft={draft}
        role="carrier"
        updateDraft={updateDraft}
        onSuccess={() => navigate('carrier-rega-password')}
        onChangePhone={() =>
          navigate(
            draft.carrierEntityType === 'company'
              ? 'carrier-rega-contact-company'
              : 'carrier-rega-contact',
          )
        }
      />
    );
  }
  if (screen === 'carrier-rega-password') {
    content = (
      <RegistrationPasswordStep
        draft={draft}
        role="carrier"
        onSuccess={() => navigate('carrier-verify')}
      />
    );
  }

  return <CarrierShell screen={screen}>{content}</CarrierShell>;
}
